"use client";

import { ArrowLeft, Calendar, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/40" />
        <div className="h-6 w-1/2 animate-pulse rounded-lg bg-muted/40" />
        <div className="h-80 w-full animate-pulse rounded-lg bg-muted/40" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse rounded bg-muted/40" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/40" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted/40" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-card-foreground">Sorry, we couldn't load this article right now.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-border bg-card p-8 text-center">
        <p className="font-default text-2xl font-semibold text-card-foreground">No post found</p>
        <p className="mt-2 text-muted-foreground">The article you're looking for doesn't exist.</p>
        <Link to="/HomePage" className="mt-6 inline-block">
          <Button variant="default">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitials = post.author?.name
    ? post.author.name
        .split(" ")
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "LF";

  return (
    <article className="mx-auto max-w-4xl">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center rounded-lg bg-accent px-3 py-1 text-xs font-medium text-accent-foreground"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
        {post.title}
      </h1>

      {}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        {post.author?.name && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <>
            <span className="text-border">•</span>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{formattedDate}</span>
            </div>
          </>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mt-10 overflow-hidden rounded-lg shadow-lg">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </AspectRatio>
        </div>
      )}

      {}
      {post.excerpt && (
        <div
          className="mt-10 border-l-4 border-primary pl-6 font-serif text-xl italic leading-relaxed text-foreground/80"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg mt-10 max-w-none text-foreground [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:font-default [&_h2]:mt-10 [&_h2]:text-3xl [&_h2]:font-semibold [&_h3]:font-default [&_h3]:mt-8 [&_h3]:text-2xl [&_h3]:font-semibold [&_img]:my-6 [&_img]:rounded-lg [&_p]:my-4 [&_p]:leading-relaxed [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Tags:</span>
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="inline-flex items-center rounded-lg border border-border bg-muted px-3 py-1 text-xs text-muted-foreground"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="bg-card">
          <CardContent>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Avatar className="h-16 w-16">
                {post.author.avatar && post.author.avatar["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground font-default text-lg font-semibold">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Written by
                </p>
                <h3 className="font-default text-xl font-semibold text-card-foreground">
                  {post.author.name}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Lush Fashion editorial team bringing you the latest trends and style guides.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div data-index={i} key={i} className="h-72 animate-pulse rounded-lg bg-muted/40" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group block"
        >
          <Card className="h-full overflow-hidden bg-card transition-shadow hover:shadow-lg">
            {post.featuredImage && (
              <AspectRatio ratio={16 / 10}>
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </AspectRatio>
            )}
            <CardContent className="space-y-3">
              {post.categories && post.categories.length > 0 && (
                <span className="inline-flex items-center rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground">
                  {post.categories[0].name}
                </span>
              )}
              <h3 className="font-default text-xl font-semibold leading-snug text-card-foreground group-hover:text-primary">
                {post.title}
              </h3>
              {post.date && (
                <p className="text-xs text-muted-foreground">
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="1514"
      id="single-post-template-section"
      className="relative w-full overflow-hidden bg-background py-16 md:py-24"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-lg bg-accent/40 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 -right-32 h-96 w-96 rounded-lg bg-secondary/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/3 h-80 w-80 rounded-lg bg-primary/10 blur-3xl"
      />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div className="mx-auto mb-10 max-w-4xl">
          <Link to="/ShopPage" className="inline-flex">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Shop
            </Button>
          </Link>
        </div>

        {}
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-20 md:mt-28">
          <div className="mx-auto mb-10 max-w-4xl">
            <p className="text-sm font-medium uppercase tracking-widest text-primary">
              Explore More
            </p>
            <h2 className="font-default mt-2 text-3xl font-bold text-foreground md:text-4xl">
              Latest Fashion Trends
            </h2>
            <p className="mt-3 text-muted-foreground">
              Discover the latest style inspirations and seasonal collections from Lush Fashion.
            </p>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}